import {Component, ElementRef, OnDestroy, OnInit} from '@angular/core';
import {FormBuilder, FormGroup} from '@angular/forms';
import {LabOrderBusinessService} from '../../shared/lab-order-business.service';
import {Router} from '@angular/router';
import {SearchCriteria} from '../../shared/data/search-criteria';
import {SearchResult} from '../../shared/data/search-result';
import {SearchCriteriaValidationService} from '../../shared/searchcriteria-validation.service';
import {Observable} from 'rxjs';
import {LabOrder} from '../../shared/data/lab-order';
import {of} from 'rxjs/observable/of';
import {catchError} from 'rxjs/operators';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {DataConstants} from '../../shared/data/data-constants';
import {LensCatalogBusinessService} from '../../shared/lens-catalog-business.service';
import {PrintPdfService} from '../../shared/print-pdf.service';
import {SubscriptionsContainer} from '../../shared/subscriptions-container';
import {SpinnerService} from './spinner.service';
import { ClearMessagesService } from '../../shared/clear-messages.service';


const TOGGLE_SELECT_ALL = 'Select All';
const TOGGLE_DESELECT_ALL = 'Deselect All';
const ACTION_ACCEPT_ORDERS = 'Accept Selected Orders';
const ACTION_COMPLETE_ORDERS = 'Complete Selected Orders';
const SEARCH_TITLE_NEW_ORDERS = 'Results for New Orders';
const SEARCH_TITLE_ACCEPT_ORDERS = 'Results for Accepted Orders';
const SEARCH_TITLE_COMPLETED_ORDERS = 'Results for Completed Orders';

@Component({
  selector: 'app-search',
  templateUrl: './search.component.html',
  styleUrls: ['./search.component.css']
})
export class SearchComponent implements OnInit, OnDestroy {

  searchForm: FormGroup;
  acceptOrdersForm: FormGroup;
  results: SearchResult;
  totalResults: number = -1;
  message: SafeHtml;
  pagingHref: string;
  lastSearchStatus = '';
  currentToggleStr: string = TOGGLE_SELECT_ALL;
  currentActionStr: string = ACTION_ACCEPT_ORDERS;
  searchTitle: string;
  spinner = false;
  spinnerPath = DataConstants.SPINNER_PATH;
  data: LabOrder;
  labOrderNumber: string;
  docDefinition;
  invalidLabOrders: string[] = [];
  subs = new SubscriptionsContainer();

  constructor(private router: Router,
              private formBuilder: FormBuilder,
              private labOrderBusinessService: LabOrderBusinessService,
              private lensCatalogBusinessService: LensCatalogBusinessService,
              private printPdfService: PrintPdfService,
              private searchCriteriaService: SearchCriteriaValidationService,
              private elementRef: ElementRef,
              private sanitizer: DomSanitizer,
              private spinnerService: SpinnerService,
              private clearMessagesService: ClearMessagesService) {
  }

  ngOnInit() {
    this.searchForm = this.formBuilder.group({
      status: 'SUBMIT_05',
      sortOrder: 'ORDER_NUMBER',
      patientLastName: '',
      invoiceNumber: ''
    });

    this.acceptOrdersForm = this.formBuilder.group({
      orderNumbers: this.formBuilder.array([])
    });

    this.subs.add = this.spinnerService.getSpinnerFlag().subscribe(result => {
      this.spinner = result;
    });

  }

  ngOnDestroy(): void {
    console.log(' Search Component - ngOnDestroy ');
    this.subs.dispose();
  }

  shouldShowSelectCheckboxes(): boolean {
    let retVal = false;

    if (this.lastSearchStatus === 'SUBMIT_05') {
      this.currentActionStr = ACTION_ACCEPT_ORDERS;
      retVal = true;
    } else if (this.lastSearchStatus === 'LAB_ACCEPTED_21') {
      this.currentActionStr = ACTION_COMPLETE_ORDERS;
      retVal = true;
    }

    return retVal;
  }

  // This method accepts the request event and processed a search request.
  public submitSearch() {
    console.log('Search for orders.  Status = ' + this.searchForm.value.status);
    console.log('Search for orders.  lastname = ' + this.searchForm.value.patientLastName);
    console.log('Search for orders.  invoice# = ' + this.searchForm.value.invoiceNumber);
    // Turn on spinner
    this.spinnerService.setSpinnerFlag(true);
    this.clearOldMessages();

    this.lastSearchStatus = this.searchForm.value.status;
    const searchStatuses: string[] = [this.searchForm.value.status];
    this.searchTitle = this.setSearchTitle(this.lastSearchStatus);
    if ('COMPLETED_PRIVATE_ORDER_98' === this.searchForm.value.status) {
      searchStatuses.push('CLAIM_READY_COMPLETED_VSP_ORDER_27');
    }
    const searchCriteria = this.buildSearchCriteria(searchStatuses);

    // if search criteria not valid, return to home page with any message.
    if (!this.searchCriteriaService.valid(searchCriteria)) {
      console.log('Message: ' + this.searchCriteriaService.getMessage());
      alert(this.searchCriteriaService.getMessage());
      return;
    }
    // Clear result before each search
    this.results = null;
    this.labOrderBusinessService.labSearch(searchCriteria)
      .subscribe(
        (searchResult) => {
          // Turn off spinner
          this.spinnerService.setSpinnerFlag(false);
          this.processSearchResults(searchResult);
          this.message = null;
        },
        (error) => {
          this.results = null;
          this.message = error.message;
          this.spinnerService.setSpinnerFlag(false);

          console.log('Error performing lab search: ' + error.message);
        }
      );
    this.currentToggleStr = TOGGLE_SELECT_ALL;
  }

  // Construct and return a SearchCriteria object instance containing all criteria entered in the searchForm
  public buildSearchCriteria(statuses: string[]): SearchCriteria {

    const searchCriteria = new SearchCriteria();

    searchCriteria.sortOrder = this.searchForm.value.sortOrder;
    statuses.forEach(value => {
      searchCriteria.statuses.push(value);
    });
    searchCriteria.lastName = this.searchForm.value.patientLastName;
    searchCriteria.invoiceNumber = this.searchForm.value.invoiceNumber;

    return searchCriteria;
  }

  // This method will first check if there is an order to view.
  public viewOrder(labOrderNumber: string, labOrderUrl: string) {

    console.log('View labOrderNumber: ' + labOrderNumber);

    this.labOrderBusinessService.viewLabOrderByUrl(labOrderUrl)
      .subscribe(
        (labOrder) => {
          console.log('Redirecting to labOrderNumber ' + labOrderNumber);

          this.router.navigate(['viewOrder/', labOrderNumber]);
        },
        (error) => {
          console.error('Unable to verify order \'' + labOrderNumber + '\' exists: ' + error.message);
          alert('Unable to find order \'' + labOrderNumber + '\'.');
        }
      );
  }

  // This function follows HATEOAS links to retrieve other pages of search results
  public jumpToPage(url: string) {

    console.log('>> url:', url);
    this.pagingHref = url;

    // is anything checked?
    if (this.isAnItemChecked()) {
      // Trigger ConfirmationModal, which will submit to submitConfirmationModal();
      const openConfirmModal = this.elementRef.nativeElement.querySelector('#openConfirmModal');
      openConfirmModal.click();
    } else {
      this.doJumpToPage();
    }
  }

  doJumpToPage() {
    // might be good to check for null and undefined url's here
    this.labOrderBusinessService.labSearchByUrl(this.pagingHref)
      .subscribe(
        (searchResult) => {
          this.processSearchResults(searchResult);
          this.message = null;
        },
        (error) => {
          this.results = null;
          this.message = error.message;

          console.log('Error performing lab search by url' + error.message);
        }
      );
  }

  submitConfirmationModal(input) {
    if (input.hasOwnProperty('confirm')) {
      this.doJumpToPage();
    }
  }

  // This function processes the result set returned from a successful search API call.
  public processSearchResults(searchResult: SearchResult) {
    const totalResults = ((searchResult) ? searchResult.totalResults : null);
    this.totalResults = (totalResults != null) ? totalResults.valueOf() : 0;
    const message = 'Successfully completed lab search. Found ' + this.totalResults + ' records.';

    console.log(message);
    console.log(searchResult);

    this.results = searchResult;
    this.message = message;
  }

  submitCheckedOrders(action: string) {
    // get array of orders that were checked
    this.spinnerService.setSpinnerFlag(true);
    this.clearOldMessages();
    const checkedOrders: string[] = this.getCheckedOrders();
    console.log('checkedOrders:', checkedOrders);

    if (checkedOrders.length > 0) {
      let invalidOrderNumbers = '';

    // get array of observables; one item per checked order
    const observables: Observable<LabOrder>[] = this.buildObservableArray(action, checkedOrders);

    // execute and process observables all at once
    Observable.forkJoin(observables)
      .subscribe(dataArray => {
        // dataArray holds the result of each observable after has resolved
        console.log(dataArray);
        // build results feedback for user in the object form of {success: '', error: ''}
        const messages: any = this.buildProcessedResultsMessages(dataArray);
        if (action === ACTION_ACCEPT_ORDERS) {
          this.printPdfService.createLabServiceReport(this.getValidLabOrderNumbers(dataArray));
        }
        if (this.invalidLabOrders.length > 0) {
          this.invalidLabOrders.forEach(orderNumber => {
            invalidOrderNumbers += !invalidOrderNumbers ? orderNumber : ', ' + orderNumber;
          });
          alert('Invalid Lab Orders: ' + invalidOrderNumbers);
        }
        const htmlToTrust: string = (messages.success ? messages.success : '') +
          (messages.error ? ((messages.success ? '<br>' : '') + messages.error) : '');
        this.message = this.sanitizer.bypassSecurityTrustHtml(htmlToTrust);

        this.results = null;
        this.spinnerService.setSpinnerFlag(false);
      });
    } else {
      this.spinnerService.setSpinnerFlag(false);
    }
  }

  toggleAllItems() {
    const elements: HTMLInputElement[] = this.getCheckedOrderElements();
    for (let i = 0; i < elements.length; i++) {
      if (elements[i].type === 'checkbox') {
        elements[i].checked = (this.currentToggleStr === TOGGLE_SELECT_ALL);
      }
    }

    if (this.currentToggleStr === TOGGLE_SELECT_ALL) {
      this.currentToggleStr = TOGGLE_DESELECT_ALL;
    } else {
      this.currentToggleStr = TOGGLE_SELECT_ALL;
    }
  }

  isAnItemChecked(): boolean {
    let retVal = false;
    const elements: HTMLInputElement[] = this.getCheckedOrderElements();

    for (let i = 0; i < elements.length; i++) {
      if (elements[i].type === 'checkbox' && elements[i].checked) {
        retVal = true;
        break;
      }
    }
    return retVal;
  }

  getCheckedOrders(): string[] {
    const checkedOrders: string[] = [];
    const elements: HTMLInputElement[] = this.getCheckedOrderElements();
    elements.forEach(element => {
      if (element.type === 'checkbox' && element.checked) {
        checkedOrders.push(element.value);
      }
    });
    return checkedOrders;
  }

  getCheckedOrderElements(): HTMLInputElement[] {
    return <HTMLInputElement[]><any>document.getElementsByName('checkedOrders');
  }

  // build array of observables; one item per checked order
  buildObservableArray(action: string, checkedOrders: string[]): Observable<LabOrder>[] {
    const observables: Observable<LabOrder>[] = [];
    checkedOrders.forEach(element => {
      if (action === ACTION_ACCEPT_ORDERS) {
        observables.push(
          this.labOrderBusinessService.acceptLabOrder(element.valueOf(), null)
            .pipe(
              catchError(err => of({
                labOrderNumber: element.valueOf(), error: err
              })))
        );
      } else if (action === ACTION_COMPLETE_ORDERS) {
        observables.push(
          this.labOrderBusinessService.completeLabOrder(element.valueOf(), null)
            .pipe(
              catchError(err => of({
                labOrderNumber: element.valueOf(), error: err
              })))
        );
      }
    });
    return observables;
  }

  buildProcessedResultsMessages(dataArray: any[]): any {
    const messages: any = {success: '', error: ''};
    // process results
    dataArray.forEach(item => {
      if (item.hasOwnProperty('error')) {
        const errObj: any = item;
        if (messages.error.length === 0) {
          messages.error = 'Not successful: ' + errObj.labOrderNumber;
        } else {
          messages.error += ', ' + errObj.labOrderNumber;
        }
      } else {
        if (messages.success.length === 0) {
          messages.success = 'Successful: ' + item.labOrderNumber;
        } else {
          messages.success += ', ' + item.labOrderNumber;
        }
      }
    });

    console.log(messages.success, messages.error);
    return messages;
  }

  setSearchTitle(status: string): string {
    let retVal = 'Orders';
    if (status === 'SUBMIT_05') {
      retVal = SEARCH_TITLE_NEW_ORDERS;
    } else if (status === 'LAB_ACCEPTED_21') {
      retVal = SEARCH_TITLE_ACCEPT_ORDERS;
    } else if (status === 'COMPLETED_PRIVATE_ORDER_98' || status === 'CLAIM_READY_COMPLETED_VSP_ORDER_27') {
      retVal = SEARCH_TITLE_COMPLETED_ORDERS;
    }
    return retVal;
  }

  buildFrameInfo(manufacturer: string, frame: string): string {
    let retVal = manufacturer ? manufacturer : '';
    retVal = (retVal && frame) ? retVal + ' - ' + frame : frame;
    return retVal;
  }

  printSelectedOrders() {
    console.log('printSelectedOrders ');
    this.clearOldMessages();
    const checkedOrders = this.getCheckedOrders();
    if (checkedOrders.length === 0) {
      alert('Please select order to print.');
      return;
    }
    this.spinnerService.setSpinnerFlag(true);
    this.printPdfService.createLabServiceReport(checkedOrders);
  }

  getValidLabOrderNumbers(dataArray: LabOrder[]) {
    const labOrderNumbers: string[] = [];
    dataArray.forEach(item => {
      if (!item.hasOwnProperty('error')) {
        labOrderNumbers.push(item.labOrderNumber);
      }else {
        this.invalidLabOrders.push(item.labOrderNumber);
      }
    });
    return labOrderNumbers;
  }

  clearOldMessages() {
    this.clearMessagesService.setClearMessages(true);
  }

}



// WEBPACK FOOTER //
// /home/jenkins-agent/workspace/ng-labportal-prod-build/src/app/orders/search/search.component.ts